<template>
  <div>
    <vs-table noDataText="" hoverFlat data="assessmentsForDisplay">
      <!-- Header row with custom style -->
      <vs-tr class="custom-row-header">
        <vs-td class="text-align-left first-column">{{ isExpanded ? $t('view_member.block.detailed_report.assessment_table_question') :'' }}</vs-td>
        <vs-td class="text-align-center" v-for="(assessment, index) in assessmentsForDisplay" :key="assessment.assessmentId + index">
          {{ assessment.assessmentDate !== 'Coming' ? formatDate(assessment.assessmentDate) : 'Coming' }}
        </vs-td>
        <vs-td class="text-align-center"><strong>{{ $t('view_member.block.detailed_report.change') }}</strong></vs-td>
      </vs-tr>

      <!-- Rows for questions and responses -->
      <vs-tr v-show="isExpanded" v-for="(question, qIndex) in uniqueQuestions" :key="qIndex" class="custom-row-normal">
        <vs-td class="text-align-left vs-bodered-cell first-column">{{question.number + '. '+ question.questionTitle }}</vs-td>
        <vs-td  class="vs-bodered-cell" v-for="assessment in assessmentsForDisplay" :key="assessment.assessmentId">
          {{ getResponse(question.questionTitle, assessment) ? getResponse(question.questionTitle, assessment) : '-' }}
        </vs-td>
        <vs-td class="vs-bodered-cell">
          <span class="mr-2"><strong>{{ calculateChange(question.questionTitle) }}</strong></span>
          <svg v-if="isChangePositive(question.questionTitle) > 0 " xmlns="http://www.w3.org/2000/svg" width="13" height="9" viewBox="0 0 13 9" fill="none">
            <path d="M6.11133 8.53369L0.0491505 -0.000854492L12.1735 -0.000854492L6.11133 8.53369Z" fill="#04E7CB"/>
          </svg>
          <svg v-else-if ="isChangePositive(question.questionTitle) < 0" xmlns="http://www.w3.org/2000/svg" width="13" height="9" viewBox="0 0 13 9" fill="none">
            <path d="M6.11133 0.154297L12.1735 8.68884H0.0491505L6.11133 0.154297Z" fill="#FF777E"/>
          </svg>
        </vs-td>
      </vs-tr>

      <!-- Spacer row with no borders -->
      <vs-tr class="custom-row-spacer" v-show="isExpanded">
        <vs-td class="custom-row-spacer" :colspan="assessmentsForDisplay.length + 2"></vs-td>
      </vs-tr>

      <!-- Total row with custom style -->
      <vs-tr class="custom-row-footer">
        <vs-td class="text-align-left"><strong>{{ $t('view_member.block.detailed_report.total') }}</strong></vs-td>
        <vs-td class="vs-bodered-cell" v-for="(total, index) in assessmentTotalForDisplay.totals" :key="index">
          <strong>{{ total }}</strong>

        </vs-td>
        <vs-td class="vs-bodered-cell">
          <span class="mr-2"><strong>{{totalChange }}</strong></span>
          <svg v-if="assessmentTotalForDisplay.totalChange < 0 " xmlns="http://www.w3.org/2000/svg" width="13" height="9" viewBox="0 0 13 9" fill="none">
            <path d="M6.11133 8.53369L0.0491505 -0.000854492L12.1735 -0.000854492L6.11133 8.53369Z" fill="#04E7CB"/>
          </svg>
          <svg v-else-if ="assessmentTotalForDisplay.totalChange > 0" xmlns="http://www.w3.org/2000/svg" width="13" height="9" viewBox="0 0 13 9" fill="none">
            <path d="M6.11133 0.154297L12.1735 8.68884H0.0491505L6.11133 0.154297Z" fill="#FF777E"/>
          </svg>
        
        </vs-td>
      </vs-tr>
    </vs-table>
  </div>
</template>
<script>
import moment from 'moment';
export default {
  name: 'SymptomReportAssessment',
  props: {
    assessmentData: {
      type: Array,
      required: true,
      default: () => []
    },
    showHistory: Boolean,
    isExpanded: Boolean
  },
  computed: {
    uniqueQuestions () {
      const questionsMap = new Map(); // Use a Map to maintain order and check for uniqueness
      let counter = 1;

      this.assessmentData.forEach(assessment => {
        assessment.userAnswers.forEach(answer => {
          if (!questionsMap.has(answer.questionTitle)) {
            // Only add if the question isn't already in the map
            questionsMap.set(answer.questionTitle, counter++);
          }
        });
      });
      // Convert map entries to an array of objects each containing the question and its number
      return Array.from(questionsMap, ([questionTitle, number]) => ({
        questionTitle,
        number
      }));
    },
        
    initialAssessments () {
      if (!this.assessmentData || this.assessmentData.length === 0) {
        return []; // Return an empty array if data is not as expected
      }
      const assessments = this.assessmentData.slice(-2); // Get the last two or fewer actual assessments
      // Check if we need to add placeholder assessments
      while (assessments.length < 2) {
        assessments.push({
          assessmentId: `placeholder-${  assessments.length}`, // Ensure unique key
          assessmentDate: 'Coming',
          userAnswers: this.uniqueQuestions.map(question => ({
            questionTitle: question,
            response: '-' // Placeholder response
          }))
        });
      }
      return assessments;
    },
    allAssessments () {
      const assessments = this.assessmentData; // Get the last two or fewer actual assessments
      // Check if we need to add placeholder assessments
      while (assessments.length < 2) {
        assessments.push({
          assessmentId: `placeholder-${  assessments.length}`, // Ensure unique key
          assessmentDate: 'Coming',
          userAnswers: this.uniqueQuestions.map(question => ({
            questionTitle: question,
            response: '-' // Placeholder response
          }))
        });
      }
      return assessments;
    },
    initialTotals () {
      const totals = this.initialAssessments.map(assessment => assessment.userAnswers.reduce((total, answer) => {
        // Parse the response or default to 0 if the response is null, undefined, or not a number
        const responseValue = parseInt(answer.response, 10);
        return isNaN(responseValue) ? '-' : (total + (isNaN(responseValue) ? 0 : responseValue));
      }, 0)
      );

      // Calculate change for the total row if there are at least two totals
      const totalChange = totals.length > 1 ? (totals[totals.length - 1] - totals[totals.length - 2]) : '0';
      return { totals, totalChange };
    },
    allTotals () {
      const totals = this.allAssessments.map(assessment => assessment.userAnswers.reduce((total, answer) => {
        // Parse the response or default to 0 if the response is null, undefined, or not a number
        const responseValue = parseInt(answer.response, 10);
        return isNaN(responseValue) ? '-' : (total + (isNaN(responseValue) ? 0 : responseValue));
      }, 0)
      );

      // Calculate change for the total row if there are at least two totals
      const totalChange = totals.length > 1 ? (totals[totals.length - 1] - totals[totals.length - 2]) : '0';
      return { totals, totalChange };
    },
    assessmentsForDisplay () {
      if (this.isExpanded && this.showHistory) {
        return this.allAssessments;
      } else {
        return this.initialAssessments;
      }
    },
    assessmentTotalForDisplay () {
      if (this.isExpanded && this.showHistory) {
        return this.allTotals;
      } else {
        return this.initialTotals;
      }
    },
    totalChange () {
      if (isNaN(this.assessmentTotalForDisplay.totalChange)) {
        return '-';
      }
      return Math.abs(this.assessmentTotalForDisplay.totalChange) !== 0 ? Math.abs(this.assessmentTotalForDisplay.totalChange) : '-';
    }
  },
  mounted () {
    this.$nextTick(() => {
      const tableBodies = document.querySelectorAll('[dir] .vs-con-table .vs-con-tbody');
      if (tableBodies.length > 0) {
        tableBodies.forEach(tbody => {
          tbody.style.background = 'transparent';
          tbody.style.border = 'none';
          tbody.style.borderRadius = '0';
        });
      }
    });
  },
  methods: {
    getResponse (question, assessment) {
      const answer = assessment.userAnswers.find(ans => ans.questionTitle === question);
      return answer ? answer.response : '-';
    },
    calculateChange (question) {
      if (this.assessmentsForDisplay.length > 1) {
        const last = this.getResponse(question, this.assessmentsForDisplay[this.assessmentsForDisplay.length - 1]);
        const secondLast = this.getResponse(question, this.assessmentsForDisplay[this.assessmentsForDisplay.length - 2]);
        if (last === null || secondLast === null || isNaN(last) || isNaN(secondLast)) {
          return '-';
        }
        return Math.abs(secondLast - last) || '0';
      }
      return '-';
    },
    isChangePositive (question) {
      if (this.assessmentsForDisplay.length > 1) {
        const last = this.getResponse(question, this.assessmentsForDisplay[this.assessmentsForDisplay.length - 1]);
        const secondLast = this.getResponse(question, this.assessmentsForDisplay[this.assessmentsForDisplay.length - 2]);
        if (last === null || secondLast === null) {
          return '-';
        }
        if (secondLast - last === 0) {
          return 0;
        }
        return (secondLast - last);
      }
    },
    formatDate (date) {
      if (!date) {
        return 'Coming';
      }
      return moment(date).format('YYYY MMM DD');
    }
  }
};
</script>

<style scoped>
.vs-bodered-cell {
  text-align: center;
  border: 1px solid #ccc;
  padding: 0px;
}
.first-column {
  width: 50%; /* Set the desired width percentage */
  min-width: 200px; /* Optional: Ensures the column does not become too narrow */
}

.custom-row-header, .custom-row-footer, .custom-row-spacer {
  background-color: #FFF; /* Set a consistent background color to counteract striping */
  border: none !important; /* Remove borders for a cleaner look */
}

.custom-row-normal:nth-child(even) {
  background-color: #EDEDED; /* Maintain striping only for normal rows */
}

.custom-row-normal:nth-child(odd) {
  background-color: #FFFFFF;
}

.text-align-left {
  text-align: left !important;
}
.text-align-center {
  text-align: center !important;
}
[dir] .vs-con-table .vs-con-tbody .vs-table--tbody-table .tr-values .vs-table--td {
  padding: 5px 7px !important;
}
</style>

